import React, { useState, useCallback, useMemo } from 'react';
import {
  Button,
  Clickable,
  Drawer,
  FormField,
  FormFileInput,
  Icon,
  P,
  Selector,
  SelectorOption,
  TextArea,
} from '@fashionnovateam/nova-ui-toolkit';
import { motion, AnimatePresence } from 'motion/react';
import { isEmpty } from 'lodash';
import {
  getContactFlowData,
  ContactFlowStep,
  ContactFlowOption,
} from './contactFlowData';
import { NavigationUser } from './Props';
import VendorAPI, { ContactFormData } from '../../Lib/Api/vendor';
import InfluencerAPI from '../../Lib/Api/influencer';
import roles from '../../Lib/Constants/roles';
import toast from '../Toast/Toast';

type FinalStepFormProps = {
  onSubmit: (description: string, files: File[]) => Promise<void>;
};

const MAX_ATTACHMENTS = 10;

function FinalStepForm({ onSubmit }: FinalStepFormProps) {
  const [description, setDescription] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  function generateUniqueId(fileName: string, index: number): string {
    return `${fileName}-${index}-${Math.random().toString(36).slice(2, 9)}`;
  }

  const handleUpload = (uploadedFile: File) => {
    setFiles((prevFiles) => [...prevFiles, uploadedFile]);
  };

  const handleRemove = (index: number) => () => {
    setFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      await onSubmit(description, files);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDescriptionChange = (
    event: React.ChangeEvent<HTMLTextAreaElement>,
  ) => {
    setDescription(event.target.value);
  };

  return (
    <div className="flex flex-col gap-4">
      <FormField
        label="Please describe your question or issue in detail"
        inputId="description"
        required
      >
        <TextArea
          id="description"
          name="description"
          value={description}
          rows={5}
          onChange={handleDescriptionChange}
          required
        />
      </FormField>
      <P size="sm" weight="medium">
        Attach any screenshots of the issue you&apos;re facing
      </P>
      {files.map((file, index) => (
        <FormFileInput
          label=""
          className="w-fit"
          key={generateUniqueId(file.name, index)}
          isUploaded
          fileName={file.name}
          getDownloadUrl={() => URL.createObjectURL(file)}
          onUpload={() => {}}
          onRemove={handleRemove(index)}
          fileTypes={['.jpg', '.jpeg', '.png', '.pdf']}
          sizeLimit={10}
        />
      ))}
      <FormFileInput
        label=""
        className="w-fit"
        inputId={`issue-attachment-${files.length}`}
        isUploaded={false}
        fileName=""
        disabled={files.length >= MAX_ATTACHMENTS}
        getDownloadUrl={() => ''}
        onUpload={handleUpload}
        onRemove={() => {}}
        error={
          files.length >= MAX_ATTACHMENTS
            ? `Maximum of ${MAX_ATTACHMENTS} attachments reached. Please remove an existing file to add a new one.`
            : undefined
        }
        fileTypes={['.jpg', '.jpeg', '.png', '.pdf']}
        sizeLimit={10}
      />
      <Button
        loading={isSubmitting}
        disabled={isEmpty(description.trim())}
        onClick={handleSubmit}
        className="mt-4"
      >
        Continue
      </Button>
    </div>
  );
}

type ContactDrawerProps = {
  isOpen: boolean;
  onClose: () => void;
  user: NavigationUser;
};

export function ContactDrawer({ isOpen, onClose, user }: ContactDrawerProps) {
  const [answers, setAnswers] = useState<string[]>([]);
  const [selectedStep, setSelectedStep] = useState<string[]>([]);
  const [finalStepTitle, setFinalStepTitle] = useState('');

  const closeContactDrawer = () => {
    setAnswers([]);
    setSelectedStep([]);
    onClose();
  };

  const currentStepId = useMemo(
    () => (answers.length > 0 ? answers[answers.length - 1] : 'contactType'),
    [answers],
  );

  const contactFlowData = useMemo(
    () => getContactFlowData(user.role),
    [user.role],
  );

  const handleOptionSelect = (nextStep: string[]) => {
    const step = contactFlowData.find(
      (s: ContactFlowStep) => s.id === currentStepId,
    );
    const selectedOption = step?.options?.find(
      (option: ContactFlowOption) => option.nextStep === nextStep[0],
    );
    setSelectedStep(nextStep);
    setTimeout(() => {
      if (selectedOption?.isFinal) {
        setAnswers([...answers, nextStep[0], 'finalStep']);
        setFinalStepTitle(selectedOption.label);
      } else {
        setAnswers([...answers, nextStep[0]]);
      }
    }, 250);
  };

  const handleGoBack = useCallback(() => {
    if (answers.length > 0) {
      setSelectedStep([]);
      setAnswers((prevAnswers) =>
        prevAnswers.slice(
          0,
          answers[answers.length - 1] === 'finalStep' ? -2 : -1,
        ),
      );
    }
  }, [answers]);

  const handleFinalSubmit = async (description: string, files: File[]) => {
    const contactFormData: ContactFormData = {
      user: {
        email: user.email,
        company_name: user.vendor?.company_name,
        first_name: user.first_name,
        last_name: user.last_name,
      },
      flow: answers.slice(0, -1),
      description,
      files,
    };

    try {
      if (user.role === roles.INFLUENCER) {
        await InfluencerAPI.submitContactForm(contactFormData);
      } else {
        await VendorAPI.submitContactForm(contactFormData);
      }
      toast(
        {
          title: "We've received your inquiry.",
          text: 'Someone from our team will get back to you as soon as possible via email.',
        },
        {
          type: 'success',
        },
      );
      closeContactDrawer();
    } catch (error) {
      toast(
        {
          title: 'Your message failed to send.',
          text: 'Please try again. If this issue continues, try again in a few hours.',
        },
        {
          type: 'error',
        },
      );
    }
  };

  const renderContactForm = () => {
    const step = contactFlowData.find(
      (s: ContactFlowStep) => s.id === currentStepId,
    );
    if (!step) return null;

    if (step.id === 'finalStep') {
      return <FinalStepForm onSubmit={handleFinalSubmit} />;
    }

    return (
      <Selector value={selectedStep} onChange={handleOptionSelect}>
        {step.options?.map((option: ContactFlowOption) => (
          <SelectorOption
            key={option.nextStep}
            value={option.nextStep}
            label={option.label}
            name={step.id}
            description={option.description}
            icon={option.icon}
          />
        ))}
      </Selector>
    );
  };

  const getCurrentTitle = useCallback(() => {
    if (answers.length === 0) {
      return 'What can we help you with?';
    }
    if (currentStepId === 'finalStep') {
      return finalStepTitle;
    }
    const step = contactFlowData.find(
      (s: ContactFlowStep) => s.id === currentStepId,
    );
    return step?.title || 'Contact Us';
  }, [answers, currentStepId, finalStepTitle]);

  return (
    <Drawer width="600px" isOpen={isOpen} onClose={closeContactDrawer}>
      <Drawer.Header>
        <Drawer.Title>{getCurrentTitle()}</Drawer.Title>
      </Drawer.Header>
      <Drawer.Content>
        <AnimatePresence mode="wait">
          <motion.div
            key={currentStepId}
            initial={{ x: 300, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: -300, opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            {renderContactForm()}
            {answers.length > 0 && (
              <Clickable onClick={handleGoBack}>
                <div className="mt-4 flex items-center gap-2 text-xs font-medium text-base-disabled">
                  <Icon name="chevron-big/chevron-big-left" />
                  Go Back
                </div>
              </Clickable>
            )}
          </motion.div>
        </AnimatePresence>
      </Drawer.Content>
    </Drawer>
  );
}
